import DashboardLayout from "@/components/layouts/DashboardLayout";
import { useUserDetails } from "@/hooks/api/useUserDetails";
import { useContactLimits, useCreateContact } from "@/hooks/api/useContacts";
import { useContactLogs } from "@/hooks/api/useContactLogs";
import { useSuggestions } from "@/hooks/api/useSuggestions";
import { useMemo, useState, useEffect } from "react";
import { User, Calendar, Clock, ArrowRight } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import AddContactFlow from "@/components/contacts/AddContactFlow";
import AddMemoryModal from "@/components/memories/AddMemoryModal";
import { AddContactSection } from "@/components/dashboard/AddContactSection";
import { AddMemorySection } from "@/components/dashboard/AddMemorySection";
import { GoogleImportFlow } from "@/components/dashboard/GoogleImportFlow";
import PersonalizeContactModal from "@/components/contacts/PersonalizeContactModal";
import { ContactPreviewCard } from "@/components/contacts/ContactPreviewCard";
import { MemoryPreviewCard } from "@/components/memories/MemoryPreviewCard";
import { QuickActions } from "@/components/dashboard/QuickActions";
import SuggestionCard from "@/components/suggestions/SuggestionCard";
import MemoryCard from "@/components/memories/MemoryCard";
import ContactCard from "@/components/contacts/ContactCard";
import { Button } from "@/components/ui/button";
import { contactsApi } from "@/api/contacts";
import { suggestionsApi } from "@/api/suggestions";
import type { Contact, ContactLog, Suggestion } from "@/types/api";
import { MemoryCardSkeleton } from "@/components/skeletons/MemoryCardSkeleton";
import { useAnalytics } from "@/hooks/useAnalytics";

const Dashboard = () => {
  const { data: userDetails, isLoading } = useUserDetails();
  const { data: contactLimits } = useContactLimits();
  const { data: contactLogs } = useContactLogs({ ordering: '-created_at', page_size: 20 });
  const { data: suggestions } = useSuggestions({ ordering: '-created_at', page_size: 5 });
  const { toast } = useToast();
  const navigate = useNavigate();
  const createContact = useCreateContact();
  const { trackButtonClick, trackUserInteraction } = useAnalytics();

  const [showAddContactFlow, setShowAddContactFlow] = useState(false);
  const [showAddMemoryModal, setShowAddMemoryModal] = useState(false);
  const [showAddContactSection, setShowAddContactSection] = useState(false);
  const [showAddMemorySection, setShowAddMemorySection] = useState(false);
  const [showGoogleImportFlow, setShowGoogleImportFlow] = useState(false);
  const [newContact, setNewContact] = useState<any>(null);
  const [showPersonalizeModal, setShowPersonalizeModal] = useState(false);

  // New sections state
  const [upcomingBirthdays, setUpcomingBirthdays] = useState<Contact[]>([]);
  const [recentMemories, setRecentMemories] = useState<ContactLog[]>([]);
  const [upcomingSuggestions, setUpcomingSuggestions] = useState<Suggestion[]>([]);
  const [previewFormState, setPreviewFormState] = useState({
    contactName: '',
    phone: '',
    relationshipType: '',
    birthday: undefined as Date | undefined,
    relationshipData: {} as Record<string, any>,
    selectedAvatar: '',
  });
  const [memoryPreviewState, setMemoryPreviewState] = useState({
    contactId: undefined as number | undefined,
    contactName: '',
    contactAvatar: '',
    memories: [] as any[],
  });

  const firstName = userDetails?.first_name || "there";
  const totalContacts = contactLimits?.current_count || 0;
  const totalMemories = contactLogs?.count || 0;

  // Calculate this week's logs count
  const thisWeekLogsCount = useMemo(() => {
    if (!contactLogs?.results) return 0;

    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Start of current week (Sunday)
    startOfWeek.setHours(0, 0, 0, 0);

    return contactLogs.results.filter(log => {
      const logDate = new Date(log.log_date);
      return logDate >= startOfWeek;
    }).length;
  }, [contactLogs]);

  // Fetch additional dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [birthdaysResponse, upcomingSuggsResponse, allContacts] = await Promise.all([
          contactsApi.getBirthdays(30),
          suggestionsApi.getUpcoming(7),
          contactsApi.list({ page_size: 100 })
        ]);

        // Handle both array and paginated response formats
        const birthdays = Array.isArray(birthdaysResponse) ? birthdaysResponse : (birthdaysResponse.results || []);
        const upcomingSuggs = Array.isArray(upcomingSuggsResponse) ? upcomingSuggsResponse : (upcomingSuggsResponse.results || []);

        setUpcomingBirthdays(birthdays.slice(0, 5));
        setUpcomingSuggestions(upcomingSuggs.slice(0, 5));

        // Create a map of contact id to avatar
        const contactAvatarMap = new Map();
        allContacts.results.forEach(contact => {
          contactAvatarMap.set(contact.id, contact.avatar);
        });

        // Add avatars to recent memories
        if (contactLogs?.results && contactLogs.results.length > 0) {
          const memoriesWithAvatars = contactLogs.results.slice(0, 5).map(memory => ({
            ...memory,
            contact_avatar: contactAvatarMap.get(memory.contact) || undefined
          }));
          setRecentMemories(memoriesWithAvatars);
        } else if (contactLogs?.results) {
          // If contactLogs has results but length is 0, still set empty array
          setRecentMemories([]);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    if (contactLogs) {
      fetchDashboardData();
    }
  }, [contactLogs]);

  const handleAddContactClick = () => {
    trackButtonClick('Add Contact', 'dashboard');
    if (contactLimits && !contactLimits.can_create) {
      toast({
        title: "Contact limit reached",
        description: contactLimits.message || "You have reached your contact limit.",
        variant: "destructive",
      });
      return;
    }
    setShowAddMemorySection(false);
    setShowGoogleImportFlow(false);
    setShowAddContactSection(true);
  };

  const handleImportFromGoogle = () => {
    trackButtonClick('Import from Google', 'dashboard');
    if (contactLimits && !contactLimits.can_create) {
      toast({
        title: "Contact limit reached",
        description: contactLimits.message || "You have reached your contact limit.",
        variant: "destructive",
      });
      return;
    }
    setShowAddContactSection(false);
    setShowAddMemorySection(false);
    setShowGoogleImportFlow(true);
  };

  const handleAddMemoryClick = () => {
    trackButtonClick('Add Memory', 'dashboard');
    setShowAddContactSection(false);
    setShowGoogleImportFlow(false);
    setShowAddMemorySection(true);
  };

  const handleCloseAddContactSection = () => {
    setShowAddContactSection(false);
    setShowGoogleImportFlow(false);
  };

  const handleGoogleImport = () => {
    setShowAddContactSection(false);
    setShowGoogleImportFlow(true);
  };

  const handleManualSave = async (contactData: any) => {
    try {
      const createdContact = await createContact.mutateAsync(contactData);
      setNewContact(createdContact);
      setShowAddContactSection(false);
      setShowPersonalizeModal(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create contact",
        variant: "destructive",
      });
    }
  };

  const handlePersonalizeComplete = () => {
    setShowPersonalizeModal(false);
    setNewContact(null);
    toast({
      title: "Success",
      description: "Contact added successfully!",
    });
  };

  const handleGoogleImportComplete = () => {
    setShowGoogleImportFlow(false);
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 md:px-8">
        <div className="py-6 md:py-8">
          <div className="mb-8">
            <h2 className="text-2xl font-medium font-euclid text-wabisabi-plum mb-2">
              Hi {firstName}, let's nurture the connections that matter to you today
            </h2>
            <p className="text-gray-600">Let's nurture the connections that matter most to you.</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-3 md:gap-6 mb-8">
            {/* Memories Card */}
            <div className="bg-white rounded-lg p-3 md:p-6 text-center">
              <h3 className="text-[12px] md:text-[14px] font-medium text-black mb-4 md:mb-[35px]">Memories</h3>
              <p className="text-[24px] md:text-[32px] font-bold text-black">{totalMemories}</p>
            </div>

            {/* People Card */}
            <div className="bg-white rounded-lg p-3 md:p-6 text-center">
              <h3 className="text-[12px] md:text-[14px] font-medium text-black mb-4 md:mb-[35px]">People</h3>
              <p className="text-[24px] md:text-[32px] font-bold text-black">{totalContacts}</p>
            </div>

            {/* This Week Card */}
            <div className="bg-white rounded-lg p-3 md:p-6 text-center">
              <h3 className="text-[12px] md:text-[14px] font-medium text-black mb-4 md:mb-[35px]">This Week</h3>
              <p className="text-[24px] md:text-[32px] font-bold text-black">{thisWeekLogsCount}</p>
            </div>
          </div>

          {/* Quick Actions Section */}
          <QuickActions
            onAddContact={handleAddContactClick}
            onImportGoogle={handleImportFromGoogle}
            onAddMemory={handleAddMemoryClick}
            showImportGoogle={true}
            showSetReminder={false}
          />

          {/* Suggestions Section or Add Contact Section or Add Memory Section */}
          <div className="mb-8">
            {showAddContactSection ? (
              <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-6">
                <AddContactSection
                  onClose={handleCloseAddContactSection}
                  onGoogleImport={handleGoogleImport}
                  onManualSave={handleManualSave}
                  onFormStateChange={setPreviewFormState}
                  skipSelection={true}
                />
                {previewFormState.contactName && (
                  <ContactPreviewCard
                    contactName={previewFormState.contactName}
                    phone={previewFormState.phone}
                    relationshipType={previewFormState.relationshipType}
                    birthday={previewFormState.birthday}
                    relationshipData={previewFormState.relationshipData}
                    avatar={previewFormState.selectedAvatar}
                  />
                )}
              </div>
            ) : showAddMemorySection ? (
              <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-6">
                <AddMemorySection
                  onClose={() => setShowAddMemorySection(false)}
                  onSuccess={() => setShowAddMemorySection(false)}
                  onFormStateChange={setMemoryPreviewState}
                />
                {memoryPreviewState.contactName && (
                  <div className="bg-white rounded-lg p-6 w-[380px]">
                    <h4 className="text-[20px] font-bold text-black mb-6">
                      Recent memories of {memoryPreviewState.contactName}
                    </h4>
                    {memoryPreviewState.memories.length > 0 ? (
                      <div className="space-y-3">
                        {memoryPreviewState.memories.map((memory) => (
                          <MemoryCard
                            key={memory.id}
                            memory={{
                              ...memory,
                              contact_avatar: memoryPreviewState.contactAvatar,
                              contact_name: memoryPreviewState.contactName,
                            }}
                            variant="compact"
                            showAvatar={true}
                            showSuggestionCount={false}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 py-4">No memories yet for this contact</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Suggestions */}
                <div className="bg-white rounded-lg p-6 lg:col-span-2">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-[20px] font-bold font-euclid text-wabisabi-plum">Suggestions</h3>
                    <Button
                      onClick={() => navigate('/suggestions')}
                      variant="ghost"
                      className="text-sm text-wabisabi-plum hover:text-wabisabi-plum"
                      size="sm"
                    >
                      See All <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                  {suggestions?.results && suggestions.results.length > 0 ? (
                    <div className="space-y-3 py-2">
                      {suggestions.results.slice(0, 5).map((suggestion) => (
                        <SuggestionCard key={suggestion.id} suggestion={suggestion} showCounts={true} showAvatar={true} />
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500">No suggestions available</p>
                  )}
                </div>

                {/* Recent Memories */}
                <div className="bg-white rounded-lg p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-[20px] font-bold font-euclid text-wabisabi-plum">
                      Recent Memories
                    </h3>
                    <Button
                      onClick={() => navigate('/memories')}
                      variant="ghost"
                      className="text-sm text-wabisabi-plum hover:text-wabisabi-plum"
                      size="sm"
                    >
                      See All <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                  {!contactLogs ? (
                    <div className="space-y-3 py-2">
                      <MemoryCardSkeleton variant="compact" showAvatar={true} />
                      <MemoryCardSkeleton variant="compact" showAvatar={true} />
                      <MemoryCardSkeleton variant="compact" showAvatar={true} />
                    </div>
                  ) : recentMemories.length > 0 ? (
                    <div className="space-y-3 py-2">
                      {recentMemories.slice(0, 5).map((memory) => (
                        <MemoryCard
                          key={memory.id}
                          memory={memory}
                          variant="compact"
                          showAvatar={true}
                          showSuggestionCount={true}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-4">No memories yet</p>
                  )}
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Add Contact Flow */}
      <AddContactFlow
        isOpen={showAddContactFlow}
        onClose={() => setShowAddContactFlow(false)}
      />

      {/* Add Memory Modal */}
      <AddMemoryModal
        isOpen={showAddMemoryModal}
        onClose={() => setShowAddMemoryModal(false)}
      />

      {/* Google Import Flow */}
      {showGoogleImportFlow && (
        <GoogleImportFlow
          onComplete={handleGoogleImportComplete}
          onCancel={() => setShowGoogleImportFlow(false)}
        />
      )}

      {/* Personalize Modal for Manual Add */}
      {showPersonalizeModal && newContact && (
        <PersonalizeContactModal
          isOpen={showPersonalizeModal}
          onClose={() => setShowPersonalizeModal(false)}
          contact={newContact}
          onSuccess={handlePersonalizeComplete}
        />
      )}
    </DashboardLayout>
  );
};

export default Dashboard;
